import { AppDispatch } from '../..';
import { AppThunk } from '../../types';
import apis from '../../../utils/apis';
import { handleApiError } from '../utils/utils';
import { showFeedbackModal } from '../UserFeedbackActions';
import { hideModal } from '../modalActions';

interface DraftData {
  DraftTypeName: string;
  Version: number;
  Content: string;
  Status?: string;
}

export const getDraft = (
  caseId: string,
  draftId: number,
  version?: number
): AppThunk<Promise<DraftData | null>> => {
  return async (dispatch) => {
    try {
      const response = await apis.get('/draft/get', {
        params: { case_id: caseId, draft_id: draftId, version },
      });
      if (response.status === 200) {
        const draftData = response.data;
        dispatch({
          type: 'UPDATE_CASE_DRAFT',
          payload: {
            caseId,
            draftId,
            version: draftData.Version,
            content: draftData.ContentHtml,
          },
        });
        return draftData;
      } else if (response.status === 404) return null;
      handleApiError(response, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

export const updateDraft = (
  caseId: string,
  draftId: number,
  version: number,
  content: string,
  status?: string
): AppThunk<Promise<void>> => {
  return async (dispatch) => {
    try {
      const response = await apis.post('/draft/update', {
        case_id: caseId,
        draft_id: draftId,
        version,
        content,
        status,
      });
      if (response.status === 200) {
        dispatch({
          type: 'UPDATE_CASE_DRAFT',
          payload: { caseId, draftId, version, content, status },
        });
        dispatch(
          showFeedbackModal({
            modalType: 'success',
            duration: 3000,
            showModal: true,
            message: 'Draft saved',
          })
        );
      } else {
        handleApiError(response, dispatch);
      }
    } catch (err) {
      handleApiError(err, dispatch);
    }
  };
};

export const markDraftAsFinal = (
  caseId: string,
  draftId: number,
  version: number
): AppThunk<Promise<void>> => {
  return async (dispatch) => {
    try {
      const response = await apis.post('/draft/mark_final', {
        case_id: caseId,
        draft_id: draftId,
        version,
      });
      if (response.status === 200) {
        dispatch({
          type: 'UPDATE_CASE_DRAFT_STATUS',
          payload: { caseId, draftId, version, status: 'Final' },
        });
      } else {
        handleApiError(response, dispatch);
      }
    } catch (err) {
      handleApiError(err, dispatch);
    }
  };
};

export const fetchDraftVersions = (
  caseId: string,
  draftId: number
): AppThunk<Promise<any>> => {
  return async (dispatch) => {
    try {
      const response = await apis.get('/draft/versions', {
        params: { case_id: caseId, draft_id: draftId },
      });
      if (response.status === 200) return response.data;
      else if (response.status === 404) return [];
      handleApiError(response, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

export const downloadDraft = (
  caseId: string,
  draftId: number,
  version: number,
  htmlContent: string,
  format: 'docx' | 'pdf',
  branding: boolean,
  topLevelTypeName: string
): AppThunk<Promise<void>> => {
  return async (dispatch) => {
    try {
      const response = await apis.post(
        '/draft/download',
        {
          case_id: caseId,
          draft_id: draftId,
          version,
          html_content: htmlContent,
          format,
          branding,
          top_level_type_name: topLevelTypeName,
        },
        { responseType: 'blob' }
      );

      const mimeType =
        format === 'docx'
          ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          : 'application/pdf';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Draft_${draftId}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      handleApiError(err, dispatch);
    }
  };
};

export const finalizeDraft = (
  matterUuid: string,
  draftTypeId: string,
  brandingTemplateId?: string
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    try {
      const requestBody = brandingTemplateId ? { branding_template_id: brandingTemplateId } : {};
      const response = await apis.post(
        `/drafts/${matterUuid}/${draftTypeId}/finalize`,
        requestBody
      );

      if (response.status === 200) {
        const completionTime = new Date().toLocaleString('en-GB', {
          dateStyle: 'short',
          timeStyle: 'medium',
          hour12: false
        });

        dispatch(
          showFeedbackModal({
            showModal: true,
            message: `Draft finalized at ${completionTime}`,
            modalType: 'success',
            duration: 4000
          })
        );

        // Log completion
        console.log(`[DRAFT_FINALIZED] ${draftTypeId} finalized at ${completionTime}`, {
          matterUuid,
          draftTypeId,
          completionTime,
          filesFolder: response.data.files_folder_id,
          filesFile: response.data.files_file_id
        });

        return true;
      } else {
        handleApiError(response, dispatch);
        return false;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return false;
    }
  };
};

export const unfinalizeDraft = (
  matterUuid: string,
  draftTypeId: string
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    try {
      const response = await apis.post(
        `/drafts/${matterUuid}/${draftTypeId}/unfinalize`
      );

      if (response.status === 200) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: 'Draft restoration initiated',
            modalType: 'success',
            duration: 3000
          })
        );

        console.log(`[DRAFT_UNFINALIZED] ${draftTypeId} restoration initiated`, {
          matterUuid,
          draftTypeId
        });

        return true;
      } else {
        handleApiError(response, dispatch);
        return false;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return false;
    }
  };
};
